import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ValuesDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
    DeskField,
    DeskEmpty,
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
} from '../../../components/staff/values/ValuesDesk';
import ConfirmModal from '../../../components/ConfirmModal';
import { getAllCategories, createCategory, deleteCategory } from '../../../services/api';

function Categories({ userData }) {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [iconUrl, setIconUrl] = useState('');
    const [saving, setSaving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const load = useCallback(async () => {
        try {
            const response = await getAllCategories();
            if (response.success) {
                setCategories(response.categories);
            } else {
                toast.error('Failed to load categories');
            }
        } catch {
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (!userData?.hasStaffRole) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();

        if (!name.trim() || !iconUrl.trim()) {
            toast.error('A name and an icon URL are both needed');
            return;
        }

        setSaving(true);
        try {
            const response = await createCategory(name.trim(), iconUrl.trim());
            if (response.success) {
                toast.success('Category created');
                setName('');
                setIconUrl('');
                load();
            } else {
                toast.error(response.message || 'Failed to create that category');
            }
        } catch {
            toast.error('Failed to create that category');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        try {
            const response = await deleteCategory(pendingDelete.id);
            if (response.success) {
                toast.success('Category deleted');
                load();
            } else {
                toast.error(response.message || 'Failed to delete that category');
            }
        } catch {
            toast.error('Failed to delete that category');
        } finally {
            setPendingDelete(null);
        }
    };

    return (
        <ValuesDesk userData={userData}>
            <ConfirmModal
                isOpen={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                title="Delete category"
                message={'Delete "' + (pendingDelete?.name || '') + '"? Categories still holding furni cannot be deleted.'}
            />

            <DeskRibbon>
                <DeskTitle>Categories</DeskTitle>
                <DeskNote>
                    {categories.length} categor{categories.length === 1 ? 'y' : 'ies'} grouping the public values page
                </DeskNote>
            </DeskRibbon>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
                <DeskPanel title="New category">
                    <form onSubmit={submit} className="flex flex-col gap-3">
                        <DeskField label="Name" required hint="e.g. Super Rares">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={DESK_INPUT_CLASS}
                                placeholder="Category name"
                            />
                        </DeskField>

                        <DeskField label="Icon URL" required hint="a small image shown beside the name">
                            <input
                                type="url"
                                value={iconUrl}
                                onChange={(e) => setIconUrl(e.target.value)}
                                className={DESK_INPUT_CLASS}
                                placeholder="https://…/icon.png"
                            />
                        </DeskField>

                        {iconUrl.trim() && (
                            <div className="flex items-center gap-2 border border-gray-200 bg-gray-50 p-2">
                                <img
                                    src={iconUrl.trim()}
                                    alt="Icon preview"
                                    style={{ imageRendering: 'pixelated', width: 'auto', height: 'auto', maxWidth: 'none', maxHeight: 'none' }}
                                    onError={(e) => { e.target.style.visibility = 'hidden'; }}
                                />
                                <DeskNote>Icon preview</DeskNote>
                            </div>
                        )}

                        <div>
                            <button type="submit" disabled={saving} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                                {saving ? 'Creating…' : 'Create category'}
                            </button>
                        </div>
                    </form>
                </DeskPanel>

                <div className="xl:col-span-2">
                    <DeskPanel title={'All categories (' + categories.length + ')'} padding="p-0">
                        {loading ? (
                            <div className="space-y-2 p-3">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <div key={i} className="h-10 animate-pulse bg-gray-100" />
                                ))}
                            </div>
                        ) : categories.length === 0 ? (
                            <DeskEmpty>No categories yet</DeskEmpty>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b-2 border-gray-300 bg-gray-100">
                                            <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Icon</th>
                                            <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Name</th>
                                            <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] sm:table-cell">Created by</th>
                                            <th className="px-3 py-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map(category => (
                                            <tr key={category.id} className="border-b border-gray-200 hover:bg-gray-50">
                                                <td className="px-3 py-2">
                                                    <div className="flex h-[40px] w-[60px] items-center justify-center overflow-hidden">
                                                        <img
                                                            src={category.iconUrl}
                                                            alt={category.name}
                                                            style={{ imageRendering: 'pixelated', width: 'auto', height: 'auto', maxWidth: 'none', maxHeight: 'none' }}
                                                            onError={(e) => { e.target.style.visibility = 'hidden'; }}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-[13px] font-bold text-gray-900">{category.name}</td>
                                                <td className="hidden px-3 py-2 text-[12px] text-gray-500 sm:table-cell">{category.createdBy}</td>
                                                <td className="px-3 py-2 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => setPendingDelete(category)}
                                                        className={DESK_BUTTON_CLASS + ' text-red-700'}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </DeskPanel>
                </div>
            </div>
        </ValuesDesk>
    );
}

export default Categories;
